/**
 * Why a ride was cancelled, and what each audience may read of it (W5-HOTFIX, D382).
 *
 * `Ride.cancellationReason` is written by every actor that can end a ride: the rider (`rider_cancelled`, `no_driver`),
 * the driver (`driver_cancelled`, `no_show` and the driver app's picks), the partner desk (`desk_*`,
 * `partner_desk_cancelled`, `PARTNER_ORDER_NOT_CREATED`), the system (`DESK_RIDE_NO_DRIVER_TIMEOUT`) and an operator.
 * Until D382 the operator (support) cancel stored the agent's free-text note there — the Admin sent `code: note` — and
 * the rider's ride view returned it. Two rules close that:
 *
 *  1. **Write side.** An operator cancel takes one code from the closed {@link OPS_CANCEL_REASON_CODES} vocabulary and
 *     an optional note of at most {@link OPS_CANCEL_NOTE_MAX_LENGTH} characters ({@link validateOpsCancelRequest}).
 *     Only the code becomes the ride's `cancellationReason`; the note goes to the internal operator audit only.
 *  2. **Read side.** Every surface a rider, a driver or a partner desk reads shows
 *     {@link participantCancellationReason}: the stored value when it is on the {@link PARTICIPANT_CANCELLATION_REASONS}
 *     allowlist; for the legacy `code: note` format, the allowlisted code before the colon; otherwise a generic code by
 *     who cancelled ({@link CANCELLED_BY_ACTOR} — an operator's unknown text reads `ops_cancelled`, "cancelled by
 *     support"). Never the stored text, so rows written before D382 (staging holds some) are covered too.
 *
 * Operator surfaces (the support ride view, the ops audit) keep the stored value: they are internal.
 * Neutral: codes only — the clients map a code to copy in their own catalogs; no locale, country or currency literal.
 */
import { err, ok } from '@vello/core';
/** Why an operator (the support agent) cancels a ride — the closed vocabulary the Admin offers and the ride stores. */
export const OPS_CANCEL_REASON_CODES = [
    /** The rider asked support to cancel. */
    'ops_rider_request',
    /** The driver asked support to cancel. */
    'ops_driver_request',
    /** A safety concern reported to support. */
    'ops_safety_concern',
    /** The same trip was requested twice. */
    'ops_duplicate_request',
    /** A payment problem blocks the trip. */
    'ops_payment_issue',
    /** The start PIN locked after five wrong attempts (D399, ADR-0064): support ends the ride, nobody at fault, no fee. */
    'ops_start_pin_locked',
    /** Anything else (the agent writes the detail in the internal note). */
    'ops_other',
];
export function isOpsCancelReasonCode(v) {
    return typeof v === 'string' && OPS_CANCEL_REASON_CODES.includes(v);
}
/** The longest internal note an operator cancel keeps (characters, after trimming). */
export const OPS_CANCEL_NOTE_MAX_LENGTH = 500;
/**
 * Whether a note is text the operator audit can keep (W5H-01, review of D382): no control character other than a tab,
 * a line feed or a carriage return (a NUL is refused by PostgreSQL's jsonb; the others have no place in an audit people
 * and exports read), and no unpaired UTF-16 surrogate (also refused by jsonb). The note is written ONLY to the audit,
 * and since D409 (W7-CANCEL-INTEGRITY) in the cancel's own transaction — so a note the audit cannot hold would roll the
 * cancel back with a 500. It is refused first, with a stable reason code, before anything is attempted.
 */
export function isStorableOpsCancelNote(note) {
    for (let i = 0; i < note.length; i += 1) {
        const c = note.charCodeAt(i);
        if ((c < 0x20 && c !== 0x09 && c !== 0x0a && c !== 0x0d) || c === 0x7f)
            return false;
        if (c >= 0xd800 && c <= 0xdbff) {
            const next = note.charCodeAt(i + 1);
            if (!(next >= 0xdc00 && next <= 0xdfff))
                return false;
            i += 1; // a well-formed pair
        }
        else if (c >= 0xdc00 && c <= 0xdfff) {
            return false;
        }
    }
    return true;
}
const present = (v) => v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === '');
/**
 * Validate an operator cancel body. `code` is the field; `reason` is read as a legacy alias of it (an Admin bundle
 * older than D382 sent `reason`) under the SAME closed validation, so `reason: "ops_other: a note"` is refused like any
 * free text. When both are sent they must agree.
 */
export function validateOpsCancelRequest(input) {
    const hasCode = present(input.code);
    const hasReason = present(input.reason);
    if (!hasCode && !hasReason)
        return err('OPS_CANCEL_REASON_REQUIRED');
    const raw = hasCode ? input.code : input.reason;
    if (!isOpsCancelReasonCode(raw))
        return err('OPS_CANCEL_REASON_UNKNOWN');
    if (hasCode && hasReason && input.reason !== input.code)
        return err('OPS_CANCEL_REASON_CONFLICT');
    if (input.note !== undefined && input.note !== null && typeof input.note !== 'string')
        return err('OPS_CANCEL_NOTE_INVALID');
    const note = typeof input.note === 'string' ? input.note.trim() : '';
    if (note.length > OPS_CANCEL_NOTE_MAX_LENGTH || !isStorableOpsCancelNote(note))
        return err('OPS_CANCEL_NOTE_INVALID');
    return ok({ code: raw, ...(note !== '' ? { note } : {}) });
}
/** The generic code a participant reads when the stored reason is not on the allowlist, by who cancelled. */
export const CANCELLED_BY_ACTOR = {
    rider: 'rider_cancelled',
    driver: 'driver_cancelled',
    ops: 'ops_cancelled',
    desk: 'partner_desk_cancelled',
    system: 'system_cancelled',
};
// ---- W7-CANCEL-INTEGRITY (D409): each actor stores only the codes of its own vocabulary ----------------------------
/**
 * Why a rider cancels a ride a driver is already assigned to (W7-DRIVER-RESTORE-AND-RIDER-CANCEL, D418; owner decision
 * D408 #5): the closed list the rider app's reason picker offers. Which of them a market accepts — and whether a rider may
 * cancel after assignment at all — is the governed setting `ride.riderCancelAfterAssignment` (`rider-cancel-policy.ts`).
 * The cancel is free: no code here carries or implies a fee.
 */
export const RIDER_AFTER_ASSIGNMENT_CANCEL_REASON_CODES = [
    /** The rider's plans changed. */
    'rider_changed_plans',
    /** The driver is taking too long to arrive. */
    'rider_driver_delayed',
    /** The pickup point was wrong. */
    'rider_wrong_pickup',
    /** The driver asked the rider to cancel. */
    'rider_driver_asked_to_cancel',
    /** The rider did not feel safe. */
    'rider_safety_concern',
    /** Anything else. */
    'rider_other',
];
export function isRiderAfterAssignmentCancelReasonCode(v) {
    return typeof v === 'string' && RIDER_AFTER_ASSIGNMENT_CANCEL_REASON_CODES.includes(v);
}
/**
 * Why a rider cancels its own request (W5H-05): the route default, the rider app's `no_driver` (the search ended with
 * no driver) and, since D418, the after-assignment picks ({@link RIDER_AFTER_ASSIGNMENT_CANCEL_REASON_CODES}). A rider can
 * never store another actor's code — an `ops_*` code would read "cancelled by support".
 */
export const RIDER_CANCEL_REASON_CODES = ['rider_cancelled', 'no_driver', ...RIDER_AFTER_ASSIGNMENT_CANCEL_REASON_CODES];
/** Why a driver cancels a trip (W5H-05): the route default and the driver app's picks (`DRIVER_CANCEL_REASONS`). */
export const DRIVER_CANCEL_REASON_CODES = ['driver_cancelled', 'rider_no_show', 'wrong_address', 'safety_concern', 'vehicle_issue', 'other'];
/**
 * The reason a rider's or a driver's cancel stores: its own default when none was sent (absent, null or blank text), the
 * sent code when it is in that actor's closed vocabulary, else `RIDE_CANCEL_REASON_UNKNOWN` (free text, another actor's
 * code, a non-string) — refused before anything changes.
 */
export function participantCancelReason(actor, raw) {
    if (raw === undefined || raw === null || (typeof raw === 'string' && raw.trim() === ''))
        return ok(actor === 'rider' ? 'rider_cancelled' : 'driver_cancelled');
    const vocabulary = actor === 'rider' ? RIDER_CANCEL_REASON_CODES : DRIVER_CANCEL_REASON_CODES;
    if (typeof raw !== 'string' || !vocabulary.includes(raw))
        return err('RIDE_CANCEL_REASON_UNKNOWN');
    return ok(raw);
}
/**
 * Why a partner desk cancels a guest's order: the codes the PDV offers (`apps/pdv/src/cancel-reasons.ts`
 * `DESK_CANCEL_REASON_CODES`) and the generic {@link DESK_DEFAULT_CANCEL_REASON} an older client that sends no reason gets.
 */
export const DESK_CANCEL_REASON_CODES = ['desk_guest_changed_plans', 'desk_wrong_details', 'desk_duplicate_order', 'desk_long_wait', 'desk_other'];
export const DESK_DEFAULT_CANCEL_REASON = 'partner_desk_cancelled';
/** The longest note a desk cancel keeps (characters, after trimming) — the PDV's own bound (`MAX_CANCEL_NOTE_LENGTH`). */
export const DESK_CANCEL_NOTE_MAX_LENGTH = 160;
const DESK_CODES = new Set([...DESK_CANCEL_REASON_CODES, DESK_DEFAULT_CANCEL_REASON]);
/** A legacy `reason` in the PDV format: `code` or `code: note` (the head before the first colon is the code). */
function splitDeskReason(reason) {
    const colon = reason.indexOf(':');
    if (colon < 0)
        return { code: reason.trim() };
    const note = reason.slice(colon + 1).trim();
    return { code: reason.slice(0, colon).trim(), ...(note !== '' ? { note } : {}) };
}
/**
 * Validate a desk cancel body. `code` + `note` is the shape; `reason` is read as the legacy field the shipped PDV bundle
 * sends — `code` or `code: note` — under the SAME closed validation. Nothing at all → {@link DESK_DEFAULT_CANCEL_REASON}.
 */
export function validateDeskCancelRequest(input) {
    const hasCode = present(input.code);
    const hasReason = present(input.reason);
    if (hasCode && typeof input.code !== 'string')
        return err('DESK_CANCEL_REASON_UNKNOWN');
    if (hasReason && typeof input.reason !== 'string')
        return err('DESK_CANCEL_REASON_UNKNOWN');
    const legacy = hasReason ? splitDeskReason(input.reason) : undefined;
    const code = hasCode ? input.code.trim() : legacy?.code ?? DESK_DEFAULT_CANCEL_REASON;
    if (!DESK_CODES.has(code))
        return err('DESK_CANCEL_REASON_UNKNOWN');
    if (hasCode && legacy !== undefined && legacy.code !== code)
        return err('DESK_CANCEL_REASON_CONFLICT');
    if (input.note !== undefined && input.note !== null && typeof input.note !== 'string')
        return err('DESK_CANCEL_NOTE_INVALID');
    const explicit = typeof input.note === 'string' ? input.note.trim() : '';
    if (explicit !== '' && legacy?.note !== undefined)
        return err('DESK_CANCEL_REASON_CONFLICT');
    const note = explicit !== '' ? explicit : legacy?.note ?? '';
    if (note.length > DESK_CANCEL_NOTE_MAX_LENGTH || !isStorableOpsCancelNote(note))
        return err('DESK_CANCEL_NOTE_INVALID');
    return ok({ code: code, ...(note !== '' ? { note } : {}) });
}
/**
 * Every cancellation reason a rider, a driver or a partner desk may read. Sources: the rider route default and the
 * rider app (`no_driver`); the driver route default, the no-show command and the driver app's `DRIVER_CANCEL_REASONS`;
 * the desk route default, the PDV's `DESK_CANCEL_REASON_CODES` and the orphan-order cleanup; the desk search timeout
 * (`@vello/business` `DESK_RIDE_NO_DRIVER_TIMEOUT`); the operator vocabulary; and the generic codes above. A new code
 * written anywhere must be added here, or participants read the generic code of its actor instead (never its text).
 */
export const PARTICIPANT_CANCELLATION_REASONS = [
    // the rider (D418: and its after-assignment picks)
    'rider_cancelled', 'no_driver', ...RIDER_AFTER_ASSIGNMENT_CANCEL_REASON_CODES,
    // the driver
    'driver_cancelled', 'no_show', 'rider_no_show', 'wrong_address', 'safety_concern', 'vehicle_issue', 'other',
    // the partner desk
    'partner_desk_cancelled', 'desk_guest_changed_plans', 'desk_wrong_details', 'desk_duplicate_order', 'desk_long_wait', 'desk_other', 'PARTNER_ORDER_NOT_CREATED',
    // the system
    'DESK_RIDE_NO_DRIVER_TIMEOUT', 'system_cancelled',
    // D689 (gap 16): a ride of a multicategory request that another category's ride won (or that the request itself could not create whole).
    'MULTICATEGORY_SIBLING_WON', 'MULTICATEGORY_REQUEST_FAILED',
    // an operator (support)
    ...OPS_CANCEL_REASON_CODES, 'ops_cancelled',
];
const VISIBLE = new Set(PARTICIPANT_CANCELLATION_REASONS);
/**
 * The cancellation reason a participant reads (rule 2 above). `cancelledBy` is the actor of the ride's `cancelled` event;
 * unknown, it is taken as an operator — the only writer that ever stored free text.
 */
export function participantCancellationReason(stored, cancelledBy) {
    if (stored === undefined)
        return undefined;
    // A decoded snapshot is untrusted JSON: a non-string reason is never shown either.
    const value = stored;
    if (typeof value !== 'string')
        return CANCELLED_BY_ACTOR[cancelledBy ?? 'ops'];
    if (VISIBLE.has(stored))
        return stored;
    const colon = stored.indexOf(':');
    if (colon > 0) {
        const head = stored.slice(0, colon).trim();
        if (VISIBLE.has(head))
            return head;
    }
    return CANCELLED_BY_ACTOR[cancelledBy ?? 'ops'];
}
/** Who cancelled the ride: the actor of its last `cancelled` event (undefined when there is none). */
export function rideCancelledBy(aggregate) {
    // A decoded snapshot is untrusted JSON: tolerate one without an event list (then the actor is unknown).
    const events = aggregate.events;
    if (!Array.isArray(events))
        return undefined;
    for (let i = events.length - 1; i >= 0; i -= 1) {
        const e = events[i];
        if (e !== undefined && e.type === 'cancelled')
            return e.actor.kind;
    }
    return undefined;
}
/** The participant-facing cancellation reason of a ride aggregate (undefined when the ride holds none). */
export function participantCancellationOf(aggregate) {
    return participantCancellationReason(aggregate.ride.cancellationReason, rideCancelledBy(aggregate));
}
/**
 * The aggregate a participant may receive whole (a route that returns the ride snapshot): the same aggregate with the
 * cancellation reason replaced by {@link participantCancellationOf}. Returns the input untouched when it holds none.
 */
export function forParticipant(aggregate) {
    const stored = aggregate.ride.cancellationReason;
    if (stored === undefined)
        return aggregate;
    const shown = participantCancellationOf(aggregate);
    if (shown === stored)
        return aggregate;
    return { ...aggregate, ride: { ...aggregate.ride, cancellationReason: shown } };
}
/**
 * W7-CANCEL-INTEGRITY (D409, W5H-04): the timeline a participant (the rider, the driver) may read — every actor reduced to
 * its `kind`. A participant learns THAT support, a partner desk or the other party acted, never WHO among the platform's
 * and the desk's people (no operator or desk agent id). Event payloads are kept as they are: the `assigned` event's
 * payload still names the assigned driver (`driverId`, `vehicleId` — the ride's other party, not an operator; review
 * W7C-03 records it as a P3 residual).
 */
export function participantEvents(events) {
    return events.map((e) => ({ ...e, actor: { kind: e.actor.kind } }));
}
/**
 * The whole aggregate a participant may receive (a route that returns the ride snapshot, e.g. an idempotent replay of a
 * ride request): {@link forParticipant}'s cancellation reason AND {@link participantEvents}' kind-only actors (D409).
 */
export function participantAggregate(aggregate) {
    const shown = forParticipant(aggregate);
    // A decoded snapshot may carry no event list; there is then nothing to project.
    if (!Array.isArray(shown.events))
        return shown;
    return { ...shown, events: participantEvents(shown.events) };
}
const STORED_REASON = /^([A-Za-z][A-Za-z0-9_]*)(?:: ([\s\S]+))?$/;
/**
 * Split a stored cancellation reason — a bare code, `code: note` (the desk and the legacy operator format) or an old free
 * note — into `{ reasonCode, note }`, so a screen translates the code and shows the note as the note. Text that does not
 * start with a code token is a legacy note and carries no code. Pure read-time parsing: nothing stored changes.
 */
export function splitStoredReason(stored) {
    const text = stored.trim();
    if (text === '')
        return {};
    const m = STORED_REASON.exec(text);
    if (m === null)
        return { note: text };
    const note = m[2]?.trim();
    return note !== undefined && note !== '' ? { reasonCode: m[1] ?? '', note } : { reasonCode: m[1] ?? '' };
}
//# sourceMappingURL=cancellation.js.map